/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useEffect, useRef} from 'react'
import PropTypes from 'prop-types'
import {Stack} from '@salesforce/retail-react-app/app/components/shared/ui'
import useSiteType from '../../hooks/use-site-type'
import useRegistrationFields from '../forms/useRegistrationFields'
import Field from '../field'
import PhoneField from '../phone-field'
import PasswordRequirements, {
    generatePasswordAriaDescribedByIds
} from '../forms/password-requirements'

const RegistrationFields = ({form, prefix = 'registration_', isFirstInputFocus = false}) => {
    const {isEU} = useSiteType()
    const fields = useRegistrationFields({form, prefix})
    const password = form.watch(`${prefix}password`)
    const firstNameRef = useRef()
    const passwordRef = useRef()
    const shareConsentRef = useRef()

    useEffect(() => {
        if (form.formState.errors[`${prefix}shareConsent`]) {
            shareConsentRef.current?.scrollIntoView({behavior: 'smooth', block: 'center'})
        }
    }, [form.formState.errors[`${prefix}shareConsent`]])

    useEffect(() => {
        if (isFirstInputFocus && firstNameRef.current) {
            firstNameRef.current.focus()
        }
    }, [])

    // use-auth-modal.js: sharing password DOM input value between Modal view switching
    useEffect(() => {
        if (passwordRef.current) {
            form.setValue(`${prefix}password`, passwordRef.current.value.trim())
        }
    }, [passwordRef.current])

    return (
        <Stack spacing={24}>
            <Stack spacing={24} direction={{base: 'column', lg: 'row'}}>
                <Field {...fields.firstName} inputRef={firstNameRef} />
                <Field {...fields.lastName} />
            </Stack>
            <Field {...fields.email} />
            <Field {...fields.birthday} />
            <Field {...fields.countryCode} />
            <PhoneField {...fields.phone} />

            <Stack spacing={16}>
                <Field
                    {...fields.password}
                    inputRef={passwordRef}
                    inputProps={{
                        'aria-describedby': generatePasswordAriaDescribedByIds(prefix)
                    }}
                />
                <PasswordRequirements value={password} formPrefix={prefix} />
            </Stack>

            <Stack spacing={16}>
                {isEU && (
                    <Field
                        {...fields.shareConsent}
                        inputProps={{alignItems: 'flex-start'}}
                        inputRef={shareConsentRef}
                    />
                )}
                <Field {...fields.marketingConsent} inputProps={{alignItems: 'flex-start'}} />
            </Stack>
        </Stack>
    )
}

RegistrationFields.propTypes = {
    /** Object returned from `useForm` */
    form: PropTypes.object.isRequired,

    /** Optional prefix for field names */
    prefix: PropTypes.string,

    /** Determines if the first input should be focused */
    isFirstInputFocus: PropTypes.bool
}

export default RegistrationFields
